/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useEffect, useRef, useState } from 'react'
import { ModalProvider } from '@hashicorp/mds-react/components'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import { usePathname, useRouter } from 'lib/i18n/routing'
import { ABMModalContent } from 'app/[locale]/abm/_components/abm-tabs/modal-content'
import { getModalType } from 'app/[locale]/abm/_utils/get-modal-type'
import type { ReactNode } from 'react'
import { usePostHog } from 'posthog-js/react'
import { vercelSplit } from '@web/cms'

interface ModalWrapperProps {
	children: ReactNode
	modalId: string
	href: string
}

const ABMModalWrapper = ({ children, modalId, href }: ModalWrapperProps) => {
	const [initialIsOpen, setInitialIsOpen] = useState(null)
	const router = useRouter()
	const pathname = usePathname()
	const posthog = usePostHog()
	const hasTrackedDirectOpenRef = useRef(false)
	const parsedHref = parseUrl(href).href
	const resourceType = getModalType(vercelSplit(parsedHref))

	useEffect(() => {
		const hash = window.location.hash.slice(1)
		setInitialIsOpen(Boolean(hash && hash === modalId))
	}, [])

	useEffect(() => {
		if (!initialIsOpen) return
		if (!posthog) return
		if (hasTrackedDirectOpenRef.current) return

		// ABM Modal opens immediately on direct url visit.
		// PostHog initializes async, so capture() can fire too early.
		// Wait for onFeatureFlags() so PostHog can capture this event.
		const removeListener = posthog.onFeatureFlags(() => {
			if (hasTrackedDirectOpenRef.current) return
			hasTrackedDirectOpenRef.current = true
			posthog.capture('abm:modal_open', {
				resource_name: modalId,
				resourceType,
				entry_method: 'direct',
			})
			removeListener()
		})

		return removeListener
	}, [initialIsOpen, modalId, posthog, resourceType])

	if (initialIsOpen === null) return null

	return (
		<ModalProvider
			initialIsOpen={initialIsOpen}
			initialModalContent={<ABMModalContent href={parsedHref} />}
			onClose={() => {
				router.replace(pathname + window.location.search, { scroll: false })
			}}
		>
			{children}
		</ModalProvider>
	)
}

export { ABMModalWrapper }
